import React from "react";
import Modal from "react-bootstrap/Modal";

// Adapted from the "Bank Connect Pre-Flight Modal" design spec - a checklist so
// clients gather everything Stripe will ask for before starting, instead of
// abandoning halfway through Stripe's five onboarding screens and having to
// start over (the onboarding link is single-use). Left column is a SET
// (no order); right column is a SEQUENCE (Stripe genuinely asks in this order) -
// keep that distinction if either list is edited.
//
// Lives here rather than inside BankConnectBanner because the onboarding step
// (ConnectBankAccount) needs the same briefing - arguably more, since that's a
// client's first encounter with Stripe and the one place they can't have seen
// any of this before.
//
// Props:
//   show, onHide  - standard modal control.
//   onContinue    - hands off to Stripe. Each caller creates its own onboarding
//                   link (they set different return routes), so the modal only
//                   raises the intent.
//   connecting    - true while that hand-off is in flight.
const CHECKLIST_ITEMS = [
  {
    title: "Your EIN or SSN",
    desc: "Business: your 9-digit EIN, entered with no dashes - 123456789, not 12-3456789. Individual or sole proprietor: your SSN and date of birth."
  },
  {
    title: "Your IRS EIN letter",
    tag: "Business only",
    desc: "Form CP 575. Keep it in front of you - your legal business name must be typed exactly as it appears there."
  },
  {
    title: "A government photo ID",
    desc: "Driver's licence, state ID or passport. You may be asked to photograph the front and back, so have the physical card with you."
  },
  {
    title: "Your bank routing and account numbers",
    desc: "From a cheque or your banking app. The account must be in the same legal name as the business or person you're registering."
  },
  {
    title: "Business address and phone",
    desc: "A physical address. PO boxes are not accepted."
  },
  {
    title: "Your mobile phone",
    desc: "Stripe texts you a code to verify your identity."
  },
  {
    title: "Details of other owners",
    tag: "Business only",
    desc: "If anyone else owns 25% or more: their full name, date of birth, home address and last 4 of their SSN."
  }
];

const STRIPE_STEPS = [
  { title: "Business type", desc: "Individual, sole proprietor, LLC or corporation." },
  { title: "Business or personal details", desc: "Legal name, EIN or SSN, address and phone." },
  {
    title: "About you",
    desc: "Your name, date of birth, home address and last 4 of your SSN, as the person authorized to act for the account. Use your home address here, not the business address."
  },
  { title: "Bank account", desc: "Routing and account number - or sign in to your bank for instant verification." },
  { title: "Review and accept", desc: "Confirm everything and accept Stripe's terms." }
];

const BankConnectChecklistModal = ({ show, onHide, onContinue, connecting = false }) => (
  <Modal show={show} onHide={onHide} size="lg" centered id="bank-connect-checklist-modal">
    <Modal.Header closeButton style={{ background: 'linear-gradient(180deg, #eff5ff 0%, #f6f9ff 100%)' }}>
      <div className="d-flex align-items-center" style={{ gap: '10px' }}>
        <span className="material-symbols-outlined" style={{ color: '#1d4ed8', fontSize: '24px' }} aria-hidden="true">account_balance</span>
        <div>
          <Modal.Title style={{ fontSize: '19px', fontWeight: 700 }}>Getting paid, made simple</Modal.Title>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#667085' }}>
            A quick checklist before you connect your bank account with Stripe.
          </p>
        </div>
      </div>
    </Modal.Header>
    <Modal.Body>
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', height: '100%' }}>
            <div className="d-flex align-items-center mb-2" style={{ gap: '9px' }}>
              <span className="material-symbols-outlined" style={{ color: '#1d4ed8' }} aria-hidden="true">checklist</span>
              <div>
                <strong style={{ fontSize: '14.5px' }}>Have these ready</strong>
                <p style={{ margin: 0, fontSize: '11.5px', color: '#667085' }}>Gather before you start</p>
              </div>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {CHECKLIST_ITEMS.map((item) => (
                <li key={item.title} style={{ display: 'flex', gap: '8px' }}>
                  <span className="material-symbols-outlined" style={{ color: '#16a34a', fontSize: '17px', marginTop: '2px' }} aria-hidden="true">check_circle</span>
                  <div>
                    <span style={{ fontSize: '13px', fontWeight: 650, color: '#101828' }}>
                      {item.title}
                      {item.tag && (
                        <span style={{ marginLeft: '6px', fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', padding: '2px 6px', borderRadius: '5px', background: '#f5f3ff', color: '#5b21b6', border: '1px solid #ddd6fe' }}>
                          {item.tag}
                        </span>
                      )}
                    </span>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#384252' }}>{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="col-md-6">
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', height: '100%' }}>
            <div className="d-flex align-items-center mb-2" style={{ gap: '9px' }}>
              <span className="material-symbols-outlined" style={{ color: '#dc2626' }} aria-hidden="true">arrow_forward</span>
              <div>
                <strong style={{ fontSize: '14.5px' }}>What Stripe will ask</strong>
                <p style={{ margin: 0, fontSize: '11.5px', color: '#667085' }}>Five steps, in this order</p>
              </div>
            </div>
            <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {STRIPE_STEPS.map((step, i) => (
                <li key={step.title} style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ flex: '0 0 auto', width: '20px', height: '20px', borderRadius: '6px', background: '#eef2ff', color: '#3730a3', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {i + 1}
                  </span>
                  <div>
                    <span style={{ fontSize: '13px', fontWeight: 650, color: '#101828' }}>{step.title}</span>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#384252' }}>{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <div style={{ background: '#fefce8', border: '1.5px solid #ca8a04', borderRadius: '10px', padding: '12px 13px', height: '100%' }}>
            <strong style={{ fontSize: '12.5px', color: '#854d0e' }}>Don't skip your EIN</strong>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#854d0e' }}>
              Stripe lets you finish without it, then asks later - and pauses your payouts until you provide it.
            </p>
          </div>
        </div>
        <div className="col-md-4">
          <div style={{ background: '#fef2f2', border: '1.5px solid #f87171', borderRadius: '10px', padding: '12px 13px', height: '100%' }}>
            <strong style={{ fontSize: '12.5px', color: '#991b1b' }}>Names must match exactly</strong>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#991b1b' }}>
              Character for character with your IRS letter or SSN records - a mismatch fails verification a day or two later, not immediately.
            </p>
          </div>
        </div>
        <div className="col-md-4">
          <div style={{ background: '#eff6ff', border: '1.5px solid #3b82f6', borderRadius: '10px', padding: '12px 13px', height: '100%' }}>
            <strong style={{ fontSize: '12.5px', color: '#1e40af' }}>You can pause and come back</strong>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#1e40af' }}>
              Your progress is saved. Close this and click Continue with Stripe again to carry on.
            </p>
          </div>
        </div>
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '14px 16px' }}>
        <strong style={{ fontSize: '13px' }}>Good to know</strong>
        <ul style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: '12.5px', color: '#384252', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <li>Takes about 5-10 minutes if you have everything ready.</li>
          <li>Stripe handles this directly - SpotSaver never sees or stores your SSN, EIN or bank details.</li>
          <li>Most accounts verify within minutes; occasionally it takes 1-2 business days.</li>
          <li>You can keep using your account, but your earnings stay on hold until your bank is connected.</li>
          <li>If Stripe can't verify something it will ask for a document. That's normal, not a rejection.</li>
        </ul>
      </div>
    </Modal.Body>
    <Modal.Footer style={{ justifyContent: 'space-between' }}>
      <a
        style={{ cursor: 'pointer', fontSize: '13.5px', fontWeight: 600, color: '#667085', textDecoration: 'underline' }}
        onClick={onHide}
        id="bank-connect-modal-later-link"
      >
        I'll do this later
      </a>
      <button className="fill-btn" onClick={onContinue} disabled={connecting} id="bank-connect-modal-continue-btn">
        {connecting ? "Redirecting to Stripe..." : "I have everything — continue to Stripe"}
      </button>
    </Modal.Footer>
  </Modal>
);

export default BankConnectChecklistModal;
